import React, { useState, useCallback } from 'react';
import { resolveApiUrl } from '../apiBase';

const apiBaseUrl = resolveApiUrl();

/**
 * KanAI Feedback Widget — EU AI Act Art. 12 compliance.
 *
 * Shows thumbs up/down + "Rapportera problem" link under every KanAI response.
 * PATCHes feedback to /api/kanai/audit/{id}/action.
 *
 * Props:
 *   auditLogId: string|null  — the audit trail row ID (returned from backend)
 *   onRating?: (betyg: 'upp'|'ner') => void — valfritt: modulens egen betygsloggning
 *     anropas utöver audit-loggen, så att användaren har ett enda tumpar (Regelverk).
 */

const widgetCss = `
.kanai-feedback {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--kp-border-subtle, #F1F5F9);
  margin-top: 6px;
}
.kanai-feedback-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: var(--kp-muted, #94A3B8);
  transition: color 150ms, background 150ms;
  display: flex;
  align-items: center;
}
.kanai-feedback-btn:hover {
  background: var(--kp-bg-hover, #F1F5F9);
}
.kanai-feedback-btn.active-up {
  color: var(--kp-green, #16A34A);
}
.kanai-feedback-btn.active-down {
  color: var(--kp-red, #DC2626);
}
.kanai-feedback-report {
  font-size: 12px;
  color: var(--kp-muted, #94A3B8);
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;
  margin-left: auto;
  padding: 4px 6px;
  border-radius: 4px;
  transition: color 150ms;
}
.kanai-feedback-report:hover {
  color: var(--kp-accent, #3B82F6);
}
.kanai-feedback-sent {
  font-size: 11px;
  color: var(--kp-green, #16A34A);
  margin-left: 4px;
}
`;

function ThumbUpIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 10v12" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
    </svg>
  );
}

function ThumbDownIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 14V2" /><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z" />
    </svg>
  );
}

export default function KanAIFeedbackWidget({ auditLogId, onRating }) {
  const [rating, setRating] = useState(null); // 1=down, 2=up
  const [reported, setReported] = useState(false);
  const [sent, setSent] = useState(false);

  const patchFeedback = useCallback(async (data) => {
    if (!auditLogId) return false;
    try {
      const res = await fetch(`${apiBaseUrl}/api/kanai/audit/${auditLogId}/action`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      return res.ok;
    } catch (_) {
      // Silent — feedback is best-effort
      return false;
    }
  }, [auditLogId]);

  // Modulens egen betygsloggning (onRating) anropas först när audit-loggen
  // lyckats, och ett fel där får aldrig påverka audit-loggen (AI Act).
  const meddelaModul = useCallback((ok, betyg) => {
    if (!ok || !onRating) return;
    try { Promise.resolve(onRating(betyg)).catch(() => {}); } catch (_) { /* modulens statistik är sekundär */ }
  }, [onRating]);

  const handleThumbUp = useCallback(() => {
    if (rating === 2) return;
    setRating(2);
    setSent(true);
    patchFeedback({ feedback_rating: 2 }).then((ok) => meddelaModul(ok, 'upp'));
  }, [rating, patchFeedback, meddelaModul]);

  const handleThumbDown = useCallback(() => {
    if (rating === 1) return;
    setRating(1);
    setSent(true);
    patchFeedback({ feedback_rating: 1 }).then((ok) => meddelaModul(ok, 'ner'));
  }, [rating, patchFeedback, meddelaModul]);

  const handleReport = useCallback(() => {
    if (reported) return;
    setReported(true);
    patchFeedback({ reported_issue: true });
  }, [reported, patchFeedback]);

  if (!auditLogId) return null;

  return (
    <>
      <style>{widgetCss}</style>
      <div className="kanai-feedback">
        <button
          className={`kanai-feedback-btn${rating === 2 ? ' active-up' : ''}`}
          onClick={handleThumbUp}
          title="Bra svar"
        >
          <ThumbUpIcon />
        </button>
        <button
          className={`kanai-feedback-btn${rating === 1 ? ' active-down' : ''}`}
          onClick={handleThumbDown}
          title="Dåligt svar"
        >
          <ThumbDownIcon />
        </button>
        {sent && <span className="kanai-feedback-sent">Tack!</span>}
        <button
          className="kanai-feedback-report"
          onClick={handleReport}
          disabled={reported}
        >
          {reported ? 'Rapporterad' : 'Rapportera problem'}
        </button>
      </div>
    </>
  );
}
